import {useCallback, useEffect, useState} from "react"
import axios from "axios"
import moment from "moment"
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip,
} from "chart.js"
import "chartjs-adapter-moment"
import {Line} from "react-chartjs-2"
import {Alert, Badge, Card, Form, Spinner} from "react-bootstrap"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../context/AuthProvider"

ChartJS.register(
    CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Tooltip, Filler
)

type Examiner = {
    personId: string
    sciTitle?: string
    firstName?: string
    lastName?: string
}

type Offer = {
    moduleOfferId?: string
    moduleNumber?: string
    moduleName?: string
    courseId?: string
    courseNumber?: string
    courseName?: string
}

type Exam = {
    examId: string
    examName?: string
    semesterName?: string
    examDate?: string
    timeFrom?: string
    timeTo?: string
    examiners: Examiner[]
    offers: Offer[]
    currentParticipantCount: number | null
}

type HistoryPoint = {
    snapshotAt: string
    participantCount: number
    daysBeforeExam: number | null
}

type HistorySeries = {
    examId: string
    examName?: string
    semesterName?: string
    examDate?: string
    modules: string[]
    current: boolean
    points: HistoryPoint[]
}

type ImportState = {
    currentSnapshotAt: string
    examCount: number
    participantCount: number
}

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)))

function examinerName(examiner: Examiner): string {
    return [examiner.sciTitle, examiner.firstName, examiner.lastName]
        .filter(Boolean).join(" ")
}

export default function TucanExamArchiveComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [query, setQuery] = useState("")
    const [results, setResults] = useState<Exam[]>([])
    const [status, setStatus] = useState<ImportState | null>(null)
    const [selected, setSelected] = useState<string | null>(null)
    const [history, setHistory] = useState<HistorySeries[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
        if (!isAdmin) return
        axios.get<ImportState | null>(
            "/api/tucan-exam-archive/status", auth.axiosAuthHeader()
        ).then(response => setStatus(response.data)).catch(() => setError(true))
    }, [auth, isAdmin])

    useEffect(() => {
        if (!isAdmin || query.trim().length < 2) {
            setResults([])
            setLoading(false)
            return
        }
        setLoading(true)
        const timer = window.setTimeout(() => {
            axios.get<Exam[]>("/api/tucan-exam-archive/search", {
                ...auth.axiosAuthHeader(),
                params: {q: query.trim()},
            }).then(response => {
                setResults(response.data)
                setError(false)
            }).catch(() => setError(true)).finally(() => setLoading(false))
        }, 300)
        return () => window.clearTimeout(timer)
    }, [auth, isAdmin, query])

    const loadHistory = useCallback((examId: string) => {
        if (selected === examId) {
            setSelected(null)
            return
        }
        setSelected(examId)
        setHistory([])
        axios.get<HistorySeries[]>(
            `/api/tucan-exam-archive/exams/${encodeURIComponent(examId)}/history`,
            auth.axiosAuthHeader()
        ).then(response => setHistory(response.data)).catch(() => setError(true))
    }, [auth, selected])

    if (!isAdmin) return <div className="component">
        <Alert variant="danger">{t("adminOnly")}</Alert>
    </div>

    return <div className="component">
        <div className="mb-4">
            <h2><i className="bi bi-graph-up-arrow me-2"/>
                {t("tucanExamArchive.title")}
            </h2>
            <p className="text-muted mb-1">{t("tucanExamArchive.description")}</p>
            {status && <small className="text-muted">
                {t("tucanExamArchive.status", {
                    date: moment(status.currentSnapshotAt).format("L LTS"),
                    exams: status.examCount,
                    participants: status.participantCount,
                })}
            </small>}
        </div>

        {error && <Alert variant="danger">{t("tucanExamArchive.loadError")}</Alert>}
        <Form.Control
            type="search"
            size="lg"
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t("tucanExamArchive.search")}
            className="mb-3"
        />
        {loading && <div className="text-center py-3"><Spinner size="sm"/></div>}
        {!loading && query.trim().length >= 2 && results.length === 0 &&
            <Alert variant="info">{t("tucanExamArchive.empty")}</Alert>}

        <div className="d-grid gap-3">
            {results.map(exam => {
                const modules = unique(exam.offers.map(offer =>
                    [offer.moduleNumber, offer.moduleName].filter(Boolean).join(" – ")
                ))
                const courses = unique(exam.offers.map(offer =>
                    [offer.courseNumber, offer.courseName].filter(Boolean).join(" – ")
                ))
                const examiners = unique(exam.examiners.map(examinerName))
                const isSelected = selected === exam.examId

                return <Card key={exam.examId}>
                    <Card.Header className="d-flex justify-content-between gap-3">
                        <div>
                            <h5 className="mb-1">
                                {exam.semesterName} – {exam.examName}
                            </h5>
                            <div>{exam.examiners.map(examiner => <Badge
                                bg="secondary" className="me-1" key={examiner.personId}>
                                {examinerName(examiner)}
                            </Badge>)}</div>
                        </div>
                        {exam.currentParticipantCount !== null && <Badge
                            bg="info" text="dark" className="align-self-start">
                            {t("tucanExamArchive.registrations", {
                                count: exam.currentParticipantCount,
                            })}
                        </Badge>}
                    </Card.Header>
                    <Card.Body>
                        {exam.examDate && <strong className="d-block mb-2">
                            {moment(exam.examDate).format("LL")}
                            {exam.timeFrom ? `, ${exam.timeFrom}` : ""}
                            {exam.timeTo ? ` – ${exam.timeTo}` : ""}
                        </strong>}
                        {modules.map(module => <div className="border rounded p-2 mb-2"
                            key={`module-${module}`}>
                            <Badge bg="secondary" className="me-2">
                                {t("tucanExamArchive.module")}
                            </Badge>{module}
                        </div>)}
                        {courses.map(course => <div className="border rounded p-2 mb-2"
                            key={`course-${course}`}>
                            <Badge bg="light" text="dark" className="me-2">
                                {t("tucanExamArchive.course")}
                            </Badge>{course}
                        </div>)}
                        <button className="btn btn-outline-primary btn-sm mt-2"
                            onClick={() => loadHistory(exam.examId)}>
                            <i className="bi bi-graph-up me-1"/>
                            {isSelected
                                ? t("tucanExamArchive.hideHistory")
                                : t("tucanExamArchive.showHistory")}
                        </button>
                        {isSelected && <div className="mt-3" style={{minHeight: 240}}>
                            {history.length
                                ? <Line data={{
                                    datasets: history.map((series, index) => {
                                        const colors = [
                                            "#00838a", "#d97706", "#6d28d9", "#2563eb",
                                            "#be123c", "#15803d", "#9333ea", "#475569",
                                        ]
                                        const color = colors[index % colors.length]
                                        return {
                                            label: [
                                                series.semesterName,
                                                series.examDate
                                                    ? moment(series.examDate).format("L")
                                                    : null,
                                            ].filter(Boolean).join(" · "),
                                            data: series.points
                                                .filter(point => point.daysBeforeExam !== null)
                                                .map(point => ({
                                                    x: point.daysBeforeExam!,
                                                    y: point.participantCount,
                                                })),
                                            borderColor: color,
                                            backgroundColor: `${color}22`,
                                            borderWidth: series.current ? 3 : 1.5,
                                            pointRadius: series.current ? 2 : 1,
                                            fill: false,
                                            tension: .2,
                                        }
                                    }),
                                }} options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        x: {
                                            type: "linear",
                                            reverse: true,
                                            title: {
                                                display: true,
                                                text: t("tucanExamArchive.daysBeforeExam"),
                                            },
                                            min: 0,
                                        },
                                        y: {beginAtZero: true, ticks: {precision: 0}},
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                title: items => t(
                                                    "tucanExamArchive.daysBeforeExamValue",
                                                    {count: items[0]?.parsed.x ?? 0}
                                                ),
                                            },
                                        },
                                    },
                                }}/>
                                : <Alert variant="light">
                                    {t("tucanExamArchive.noHistory")}
                                </Alert>}
                        </div>}
                        <small className="text-muted d-block mt-2">
                            ID {exam.examId}
                            {examiners.length ? ` · ${examiners.join(", ")}` : ""}
                        </small>
                    </Card.Body>
                </Card>
            })}
        </div>
    </div>
}
